import { Card, CardContent } from '@/components/ui/Card.tsx';
import timeUtils from '@/utils/timeUtils.ts';
import TextInfoSection from '@/features/public/booking/components/TextInfoSection.tsx';
import { useQuery } from '@tanstack/react-query';
import { getBasicBookingInfoQueryOptions } from '@/features/public/booking/hooks/useGetBasicBookingInfo.ts';
import { useEffect } from 'react';
import { RegistrationStatus } from '@/features/dashboard/tours/types/RegistrationStatus.ts';
import { queryClient } from '@/App.tsx';
import { bookingApiQueryKeys } from '@/features/public/booking/api/bookingApi.queryKeys.ts';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { RefreshCw } from 'lucide-react';
import dayjs from 'dayjs';
import { BookingTourBasicDataDto } from '@/features/public/booking/types/BookingTourBasicDataDto.ts';
import { bookingRoute } from '@/features/public/booking/routes/bookingRoutes.ts';
import { useRouter } from '@tanstack/react-router';

const REFETCH_DELAY_MS = 30 * 1000;

type BookingNotOpenContentProps = {
  tourBasicInfo: BookingTourBasicDataDto;
};

const BookingNotOpenContent = ({
  tourBasicInfo,
}: BookingNotOpenContentProps) => {
  const { orgSlug, tourSlug } = bookingRoute.useParams();
  const router = useRouter();

  const hasRegistrationStartDate =
    !!tourBasicInfo.registrationDetails.registrationStartDate;

  const { data } = useQuery({
    ...getBasicBookingInfoQueryOptions(orgSlug, tourSlug),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (!hasRegistrationStartDate) return;

    const delayMs =
      dayjs(tourBasicInfo.registrationDetails.registrationStartDate).diff(
        dayjs(),
      ) + REFETCH_DELAY_MS;

    if (delayMs <= 0) return;

    const timeout = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: bookingApiQueryKeys.basicInfo(orgSlug, tourSlug),
      });
    }, delayMs);

    return () => clearTimeout(timeout);
  }, [
    hasRegistrationStartDate,
    tourBasicInfo.registrationDetails,
    orgSlug,
    tourSlug,
  ]);

  // Ten sam ekran obsługuje PLANNED i NOT_SCHEDULED — przeładowujemy route
  // dopiero, gdy status wypadnie z obu, inaczej NOT_SCHEDULED zapętliłby
  // `invalidate` przy każdym renderze.
  useEffect(() => {
    const status = data?.registrationDetails.registrationStatus;
    if (status === undefined) return;
    if (
      status !== RegistrationStatus.PLANNED &&
      status !== RegistrationStatus.NOT_SCHEDULED
    ) {
      router.invalidate();
    }
  }, [data?.registrationDetails, router]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-base-text">
          Rejestracja na <b>{tourBasicInfo.tourName}</b> nie została jeszcze
          otwarta.
        </h1>

        <p className="text-neutral-500">
          Wróć tutaj, gdy rejestracja zostanie otwarta, aby zarezerwować swoje
          miejsce na wyjazd.
        </p>
      </div>

      <ActionButton
        tone="outline"
        size="xs"
        onClick={() =>
          queryClient.invalidateQueries({
            queryKey: bookingApiQueryKeys.basicInfo(orgSlug, tourSlug),
          })
        }
      >
        <RefreshCw />
        Sprawdź ponownie
      </ActionButton>

      <Card>
        <CardContent className="space-y-6">
          <TextInfoSection
            title="Okres rejestracji"
            info={
              hasRegistrationStartDate ? (
                <>
                  <p>
                    Rozpoczęcie:{' '}
                    <span className="font-semibold">
                      {timeUtils.toDateTimeString(
                        tourBasicInfo.registrationDetails
                          .registrationStartDate!,
                      )}
                    </span>
                  </p>
                  {tourBasicInfo.registrationDetails
                    .effectiveRegistrationEndDate && (
                    <p>
                      Zakończenie:{' '}
                      <span className="font-semibold">
                        {timeUtils.toDateTimeString(
                          tourBasicInfo.registrationDetails
                            .effectiveRegistrationEndDate,
                        )}
                      </span>
                    </p>
                  )}
                </>
              ) : (
                <p className="text-neutral-500">
                  Termin automatycznego otwarcia zapisów nie został
                  skonfigurowany. Organizator może otworzyć zapisy ręcznie.
                  Skontaktuj się z nim bezpośrednio w celu uzyskania informacji
                  jakie są planowane terminy rejestacji.
                </p>
              )
            }
          />

          <TextInfoSection
            title="Termin wyjazdu"
            info={
              <>
                <p>
                  Rozpoczęcie:{' '}
                  <span className="font-semibold">
                    {timeUtils.toDateTimeString(tourBasicInfo.startDate)}
                  </span>
                </p>
                <p>
                  Zakończenie:{' '}
                  <span className="font-semibold">
                    {timeUtils.toDateTimeString(tourBasicInfo.endDate)}
                  </span>
                </p>
              </>
            }
          />

          {tourBasicInfo.tourDescription &&
            tourBasicInfo?.tourDescription?.length > 0 && (
              <TextInfoSection
                title="Opis wyjazdu"
                info={tourBasicInfo.tourDescription}
              />
            )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingNotOpenContent;
